import { InjectProperty } from '@jkhy/frontend-utils/di';
import { DynamicComponentExitReason } from '@jkhy/frontend-utils/lit/dynamic-component-registry';
import { CompanyAccountModelDto, ResearchTransactionFilterModelDto } from '@treasury/api/channel';
import { AccountsService, ResearchedTransaction } from '@treasury/domain/accounts';
import type { FdlRecord } from '@treasury/FDL/field';
import { ListeningElementMixin } from '@treasury/omega/components';
import '@treasury/omega/components/omega-button';
import '@treasury/omega/components/omega-field';
import '@treasury/omega/components/progress/omega-progress';
import { createFilterRecord } from '@treasury/omega/layouts/omega-report/omega-report.helpers';
import { OmegaToastService } from '@treasury/omega/services/omega-toast';
import {
    SideDrawerButtonPosture,
    TmSideDrawerService,
} from '@treasury/presentation/services/side-drawer';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '../../../components/blocking-loader';
import './check-lookup-filters';
import './check-lookup-table';
import { filters } from './data/check-lookup-data';
import { filterResultsToCheckLookup, mapCheckLookupFilters } from './data/check-lookup.mappings';

const ListeningElement = ListeningElementMixin(LitElement);

const tagName = 'check-lookup-widget';
@customElement(tagName)
export class CheckLookupWidget extends ListeningElement {
    @InjectProperty()
    declare private readonly AccountsService: AccountsService;

    @InjectProperty()
    declare private readonly drawerService: TmSideDrawerService;

    @InjectProperty()
    declare private readonly toastService: OmegaToastService;

    @state()
    private loading = true;

    @state()
    private fetchingRecords = false;

    @state()
    private accounts: CompanyAccountModelDto[] = [];

    @state()
    private filterRecord?: FdlRecord;

    @state()
    private applyFiltersDisabled = false;

    @state()
    private transactions: ResearchedTransaction[] = [];

    @state()
    private transactionAccountDisplayField = '';

    async firstUpdated() {
        this.loading = true;
        try {
            this.accounts = (await this.AccountsService.getAllAccounts()).map(acc =>
                acc.toDto()
            ) as CompanyAccountModelDto[];
            const widgetFilters = filters(this.accounts);
            this.filterRecord = createFilterRecord<CompanyAccountModelDto>(widgetFilters as any);
            // The transactions response doesn't contain the user preferences that the accounts endpoint does so we have to figure that out...
            // there's also no 1:1 id or unique identifier between the two responses so we have to set a property and send that to the table to figure out if we should show the account number or the account name
            // referencing workaround logic from Channel, this is nuts...
            this.filterRecord.values.accounts[0].showAccountNickName
                ? (this.transactionAccountDisplayField = 'accountName')
                : (this.transactionAccountDisplayField = 'accountNumber');
            this.listenTo(this.filterRecord, 'change', e => {
                this.applyFiltersDisabled =
                    !this.filterRecord?.hasRequiredValues() || this.fetchingRecords;
            });
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
        this.loading = false;
    }

    async fetchRecords() {
        const filters = mapCheckLookupFilters(this.filterRecord?.values);
        this.fetchingRecords = true;
        try {
            const response = await this.AccountsService.researchTransactions(
                filters as unknown as ResearchTransactionFilterModelDto
            );
            this.transactions = filterResultsToCheckLookup(response);
            this.openDrawer();
        } catch (e) {
            console.error('Error fetching records:', e);
            const errorMessage = e instanceof Error ? e.message : String(e);
            this.toastService.render([{ message: errorMessage, type: 'error' }]);
        } finally {
            this.fetchingRecords = false;
        }
    }

    openDrawer() {
        this.drawerService.open(
            html`<check-lookup-table
                .transactions=${this.transactions}
                .accountDisplayLabel=${this.transactionAccountDisplayField}
            ></check-lookup-table>`,
            'Check Lookup',
            {
                promptOnClose: false,
                buttons: [
                    {
                        label: 'New lookup',
                        reason: DynamicComponentExitReason.Cancel,
                        posture: SideDrawerButtonPosture.Primary,
                    },
                ],
            }
        );
    }

    renderBlockingLoader() {
        if (this.fetchingRecords) {
            return html`<blocking-loader></blocking-loader>`;
        }
        return nothing;
    }

    render() {
        if (this.loading) {
            return html`<div class="loader-wrapper"><omega-progress card></omega-progress></div>`;
        }
        if (!this.filterRecord) {
            return nothing;
        }
        return html`${this.renderBlockingLoader()}

            <check-lookup-filters
                id="check-lookup-filters"
                .filterRecord=${this.filterRecord}
            ></check-lookup-filters>

            <div class="footer">
                <omega-button
                    type="primary"
                    .disabled=${this.applyFiltersDisabled}
                    @click=${() => {
                        this.fetchRecords();
                    }}
                    >Lookup</omega-button
                ><omega-button
                    type="secondary"
                    @click=${() => {
                        this.filterRecord?.reset();
                        this.requestUpdate();
                    }}
                    >Reset</omega-button
                >
            </div> `;
    }

    static get styles() {
        return [
            css`
                :host {
                    display: block;
                    background-color: #fff;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    height: 100%;
                }
                #check-lookup-filters {
                    overflow: auto;
                }
                .footer {
                    border-top: 1px solid #ccc;
                    position: sticky;
                    bottom: 0;
                    background-color: #fff;
                    z-index: 2;
                    width: 100%;
                    display: flex;
                    align-items: start;
                    padding: 2px 5px;
                }
                .loader-wrapper {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 85%;
                }
                omega-field {
                    padding: 10px 10px;
                }
                #filters {
                    padding: 16px;
                }
            `,
        ];
    }
}
